import React, { useCallback, useLayoutEffect, useRef } from 'react'

import { setStatusBannerHost } from '../utils/statusBannerHost'
import {
  STATUS_BANNER_GAP_PX,
  STATUS_BANNER_TOP_MARGIN_PX,
  STATUS_BANNER_Z_INDEX,
} from './styles/statusBanner.styles'

/** Geometry of the region the user can actually see, in layout (CSS) pixels. */
interface ViewportPin {
  offsetLeft: number
  offsetTop: number
  width: number
}

/**
 * Reads the visible region, degrading to the layout viewport where there is no separate one.
 *
 * The fallback is not only for older Firefox and jsdom: under **page** zoom (Blink and Gecko
 * pinch, macOS smart zoom, Cmd/Ctrl+) the visual viewport never diverges from the layout one, so
 * every engine but WebKit reports zero offsets and takes the same path.
 */
function readPin(): ViewportPin {
  const vv = window.visualViewport
  if (!vv) {
    return { offsetLeft: 0, offsetTop: 0, width: window.innerWidth }
  }
  return { offsetLeft: vv.offsetLeft, offsetTop: vv.offsetTop, width: vv.width }
}

/**
 * The column every top-centre status banner renders into, re-anchored to the region the user can
 * actually see.
 *
 * This is the single owner of the viewport maths. Previously each banner positioned itself, and
 * because they were `position: fixed` in the layout viewport, WebKit magnification slid them
 * off-screen along with the very controls the zoom notice reports on — invisible at exactly the
 * moment it is needed. Translating the column by the visual viewport's offsets keeps the whole
 * group inside the visible region instead, and makes stacking the browser's job: the banners are
 * flex siblings ordered by `bannerFlexOrder`, so they cannot overlap, a banner that is not
 * rendering leaves no gap, and no measurement is involved.
 *
 * **It deliberately does not counter-scale.** An earlier revision multiplied in `1 / scale` so the
 * banners held a constant apparent size at any magnification. That is exactly wrong for the people
 * most likely to be affected: someone who pinch-zooms to enlarge text would have found every
 * warning stubbornly small, with no way to magnify it. Sizing the box in visual-viewport
 * coordinates — `width: vv.width` — spans the visible region while letting the content scale with
 * the page like everything else.
 *
 * Dropping the counter-scale also removed a fight we could not win. The scale was applied from JS
 * a frame after the compositor had already animated the magnification, so mid-pinch the column
 * grew and got shrunk back, over and over, worst at low magnification where each step is a large
 * relative correction. With nothing to counter, only the offsets are tracked — and panning was
 * always the smooth path.
 *
 * Mounted unconditionally and self-sizing (`height: auto`), so an empty column occupies nothing
 * and costs one listener set. It is click-through; each banner opts back into pointer events.
 */
const StatusBannerViewport: React.FC = () => {
  const nodeRef = useRef<HTMLDivElement | null>(null)

  const hostRef = useCallback((el: HTMLDivElement | null) => {
    nodeRef.current = el
    setStatusBannerHost(el)
  }, [])

  useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node) return

    /**
     * Written straight to the node rather than through React state: a pinch fires ~60
     * `visualViewport` events per second, and routing each one through `setState` costs a render
     * and reconciliation purely to change one transform string. `transform` and `width` are
     * deliberately absent from the JSX `style` below, so React never manages them and a re-render
     * cannot clobber what is written here.
     */
    const apply = () => {
      const pin = readPin()
      node.style.transform = `translate(${pin.offsetLeft}px, ${pin.offsetTop}px)`
      node.style.width = `${pin.width}px`
    }

    // In a layout effect, so the column is positioned before the first paint rather than
    // appearing at the layout origin for a frame.
    apply()

    let rafId = 0
    let pending = false
    // Flag rather than the handle: a callback that runs before the assignment completes would
    // otherwise leave a stale handle behind and swallow every later scheduling attempt.
    const schedule = () => {
      if (pending) return
      pending = true
      rafId = window.requestAnimationFrame(() => {
        pending = false
        rafId = 0
        apply()
      })
    }

    // Page zoom fires `resize`; WebKit magnification and the panning after it only fire these.
    window.addEventListener('resize', schedule)
    window.visualViewport?.addEventListener('resize', schedule)
    window.visualViewport?.addEventListener('scroll', schedule)

    return () => {
      window.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('resize', schedule)
      window.visualViewport?.removeEventListener('scroll', schedule)
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div
      ref={hostRef}
      data-testid="status-banner-viewport"
      // `transform` and `width` are set imperatively in the layout effect above and must stay out
      // of this object: React only reconciles style properties it has seen, so omitting them here
      // is what keeps a re-render from resetting the live geometry.
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: `${STATUS_BANNER_GAP_PX}px`,
        paddingTop: `${STATUS_BANNER_TOP_MARGIN_PX}px`,
        zIndex: STATUS_BANNER_Z_INDEX,
        pointerEvents: 'none',
      }}
    />
  )
}

export default StatusBannerViewport
